import { RefObject, useEffect, useRef } from 'react';

interface UseSwipeNavigationProps {
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  limitSwap?: number,
}

/**
 * Custom Hook que adiciona gestos para mover no eixo X
 *  e retorna um `RefObject` para o div desejado.
 *
 * - onSwipeLeft - Direita para esquerda
 * - onSwipeRight - Esquerda para direita
 *
 * @export
 * @param {UseSwipeNavigationProps}
 * @return {*}  {RefObject<HTMLDivElement>}
 */
export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  limitSwap = 50,
}: UseSwipeNavigationProps): RefObject<HTMLDivElement> {
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      startX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (startX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - startX.current;

      if (Math.abs(deltaX) > limitSwap) {
        if (deltaX < 0) {
          onSwipeLeft();
        } else {
          onSwipeRight();
        }
      }

      startX.current = null;
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipeLeft, onSwipeRight, limitSwap]);

  return containerRef;
}
